import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function LegalLayout({ title, eyebrow, updated, children }) {
  const { pathname } = useLocation();

  return (
    <main data-testid="legal-page" className="bg-[#050505] text-white min-h-screen">
      {/* Slim header */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="legal-brand-link" className="flex items-center gap-2 group">
            <span className="w-2.5 h-2.5 bg-yellow-400 inline-block" />
            <span className="font-heading text-sm tracking-tight font-medium">
              Invisible System Design
            </span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-400 hover:text-yellow-400 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" strokeWidth={2} />
            Back to home
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-yellow-400" />
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            {eyebrow}
          </span>
        </div>

        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-tight text-white mb-4">
          {title}
        </h1>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-600 mb-16">
          Last updated · {updated}
        </p>

        <div className="prose-legal space-y-10 text-zinc-300 font-light leading-relaxed">
          {children}
        </div>

        {/* Cross-link */}
        <div className="mt-20 pt-10 border-t border-white/10 flex flex-col sm:flex-row gap-4 justify-between text-xs uppercase tracking-[0.22em] font-mono text-zinc-500">
          <Link
            to={pathname === "/legal/privacy" ? "/legal/terms" : "/legal/privacy"}
            data-testid="legal-cross-link"
            className="hover:text-yellow-400 transition-colors"
          >
            {pathname === "/legal/privacy" ? "→ Read the Terms of Service" : "→ Read the Privacy Policy"}
          </Link>
          <a
            href="mailto:Robert@InvisibleSystem.Design?subject=Legal%20question"
            data-testid="legal-contact-link"
            className="hover:text-yellow-400 transition-colors"
          >
            Robert@InvisibleSystem.Design
          </a>
        </div>
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 md:px-10 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-600">
          © {new Date().getFullYear()} Invisible System Design · All quiet rights reserved.
        </div>
      </footer>
    </main>
  );
}

export function LegalSection({ n, title, children }) {
  return (
    <section data-testid={`legal-section-${n}`} className="grid sm:grid-cols-12 gap-6">
      <div className="sm:col-span-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-yellow-400">
          §{String(n).padStart(2, "0")}
        </span>
      </div>
      <div className="sm:col-span-10 space-y-4">
        <h2 className="font-heading text-xl sm:text-2xl font-light tracking-tight text-white">
          {title}
        </h2>
        <div className="text-base text-zinc-400 font-light leading-relaxed space-y-4">
          {children}
        </div>
      </div>
    </section>
  );
}
